import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../systems/api-client';
import {
  DEFAULT_DEPARTMENTS,
  MAX_DEPARTMENT_LABEL_LENGTH,
  MAX_DEPARTMENTS,
  type DepartmentOption,
} from '../../systems/user-departments';
import styles from './department-labels-editor.module.css';

interface DepartmentLabelsEditorProps {
  enterpriseId: string;
  onLabelsSaved?: (departments: DepartmentOption[]) => void;
  onDepartmentsReset?: () => void;
}

const DepartmentLabelsEditor: React.FC<DepartmentLabelsEditorProps> = ({
  enterpriseId,
  onLabelsSaved,
  onDepartmentsReset,
}) => {
  const [draft, setDraft] = useState<DepartmentOption[]>(() => DEFAULT_DEPARTMENTS.map((d) => ({ ...d })));
  const [usage, setUsage] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enterpriseId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMessage(null);

    (async () => {
      try {
        const response = await apiFetch(`/api/auth/admin/enterprises/${enterpriseId}/department-labels`);
        const data = await response.json() as {
          departments?: DepartmentOption[];
          usage?: Record<string, number>;
          error?: string;
        };
        if (cancelled) return;
        if (!response.ok) {
          throw new Error(data.error || 'Échec du chargement des noms');
        }
        setDraft(
          Array.isArray(data.departments) && data.departments.length > 0
            ? data.departments.map((dept) => ({ ...dept }))
            : DEFAULT_DEPARTMENTS.map((d) => ({ ...d }))
        );
        setUsage(data.usage ?? {});
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Échec du chargement des noms');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enterpriseId]);

  const updateLabel = (index: number, value: string) => {
    setDraft((prev) => prev.map((dept, i) => (i === index ? { ...dept, label: value } : dept)));
    setMessage(null);
  };

  const handleAdd = () => {
    if (draft.length >= MAX_DEPARTMENTS) return;
    setDraft((prev) => [...prev, { key: '', label: '' }]);
    setMessage(null);
  };

  const handleRemove = (index: number) => {
    if (draft.length <= 1) return;
    setDraft((prev) => prev.filter((_, i) => i !== index));
    setMessage(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const keptKeys = new Set(draft.map((dept) => dept.key).filter(Boolean));
    const removedCount = Object.entries(usage).reduce((total, [key, count]) => {
      if (keptKeys.has(key)) return total;
      return total + count;
    }, 0);
    if (removedCount > 0) {
      const confirmed = window.confirm(
        `${removedCount} utilisateur${removedCount > 1 ? 's' : ''} devront à nouveau choisir leur rôle. Continuer ?`
      );
      if (!confirmed) return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload = draft.map((dept) => (
        dept.key ? { key: dept.key, label: dept.label } : { label: dept.label }
      ));
      const response = await apiFetch(`/api/auth/admin/enterprises/${enterpriseId}/department-labels`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ departments: payload }),
      });
      const data = await response.json() as {
        departments?: DepartmentOption[];
        error?: string;
      };
      if (!response.ok) {
        throw new Error(data.error || 'Échec de l’enregistrement');
      }
      const saved = Array.isArray(data.departments) ? data.departments : draft;
      setDraft(saved.map((dept) => ({ ...dept })));
      setUsage((prev) => {
        const next: Record<string, number> = {};
        for (const dept of saved) {
          if (prev[dept.key]) next[dept.key] = prev[dept.key];
        }
        return next;
      });
      setMessage('Rôles enregistrés.');
      onLabelsSaved?.(saved);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Échec de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDepartments = async () => {
    const confirmed = window.confirm(
      'Tous les utilisateurs de ce workspace devront à nouveau choisir leur rôle. Continuer ?'
    );
    if (!confirmed) return;

    setResetting(true);
    setError(null);
    setMessage(null);
    try {
      const response = await apiFetch(`/api/auth/admin/enterprises/${enterpriseId}/reset-departments`, {
        method: 'POST',
      });
      const data = await response.json() as { error?: string };
      if (!response.ok) {
        throw new Error(data.error || 'Échec de la re-demande de rôle');
      }
      setUsage({});
      setMessage('Les utilisateurs devront choisir à nouveau leur rôle.');
      onDepartmentsReset?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Échec de la re-demande de rôle');
    } finally {
      setResetting(false);
    }
  };

  return (
    <form onSubmit={handleSave} className={styles.form}>
      {draft.map((dept, index) => (
        <label key={dept.key || `new-${index}`} className={styles.field}>
          <span className={styles.fieldLabel}>#{index + 1}</span>
          <input
            type="text"
            value={dept.label}
            onChange={(e) => updateLabel(index, e.target.value)}
            maxLength={MAX_DEPARTMENT_LABEL_LENGTH}
            className={styles.input}
            disabled={loading || saving}
            required
          />
          <button
            type="button"
            className={styles.removeBtn}
            disabled={loading || saving || resetting || draft.length <= 1}
            onClick={() => handleRemove(index)}
          >
            Supprimer
          </button>
        </label>
      ))}
      {error && <p className={styles.error}>{error}</p>}
      {message && <p className={styles.success}>{message}</p>}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.addBtn}
          disabled={loading || saving || resetting || draft.length >= MAX_DEPARTMENTS}
          onClick={handleAdd}
        >
          Ajouter un rôle
        </button>
        <button type="submit" className={styles.saveBtn} disabled={loading || saving || resetting}>
          {saving ? 'Enregistrement…' : 'Enregistrer les noms'}
        </button>
        <button
          type="button"
          className={styles.resetBtn}
          disabled={loading || saving || resetting}
          onClick={handleResetDepartments}
        >
          {resetting ? 'En cours…' : 'Redemander le rôle à tous les utilisateurs'}
        </button>
      </div>
    </form>
  );
};

export default DepartmentLabelsEditor;
